import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import FullscreenLoading from '@components/Loading';
import { RequiredAuth } from '@router/RequiredAuth';
import { RoleGuard } from '@router/RoleGuard';
import { secureRoutes } from '@router/routes/secureRoutes';
import { defaultItems, defaultSubtitle, defaultTitle } from '@pages/Forbidden/Forbidden.constants';
import { Forbidden } from '@pages/Forbidden/Forbidden';
import { useUserRoles } from '@services/userRoles';

import { IRoute } from '@router/routes/Route.interface';
import { RootLayout } from '@layout/RootLayout';
import { GLOBAL_GET_APPOINTED_SLUG } from '@pages/GetAppointed/constants';
import { PortalAdminPages, PortalAdminPageName } from '@pages/PortalAdmin/PortalAdmin.constants';
const Login = lazy(() => import('@pages/Login/Login').then((m) => ({ default: m.Login })));
const GetAppointed = lazy(() => import('@pages/GetAppointed').then((m) => ({ default: m.GetAppointed })));
const GetAppointedPreview = lazy(() => import('@pages/GetAppointed').then((m) => ({ default: m.GetAppointedPreview })));
const LoginCallbackLazy = lazy(() => import('./LoginCallbackWrapper'));
import { LogoutPage } from '@components/logout/LogoutPage';
import { LoginView } from '@pages/Login/constants';
import { IOutlet } from '@router/routes/Route.interface';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';

const renderRoutes = (parentRoute: IRoute, currentRoute: IOutlet, defaultRoles: string[], depth: number = 0) => {
  const path = currentRoute.path;
  const key = path ? `${parentRoute.path}/${path}` : `${parentRoute.path}-index`;

  return (
    <Route
      key={key}
      path={path}
      element={
        <RoleGuard outlet routeSpecificRoles={currentRoute.roles ?? (depth > 0 ? parentRoute.roles : defaultRoles)}>
          {currentRoute.component}
        </RoleGuard>
      }
    >
      {currentRoute.indexPath && <Route index element={<Navigate to={currentRoute.indexPath} replace />} />}

      {currentRoute.outlets?.map((child) => renderRoutes(currentRoute as IRoute, child, defaultRoles, depth + 1))}
    </Route>
  );
};

function AllRoutes() {
  const { oktaAuth } = useOktaAuth();
  useUserRoles();

  const { isUnderwriter, isInternalUser } = useStore((state) => state.userAuth, shallow);

  return (
    <Suspense fallback={<FullscreenLoading />}>
      <Routes>
        <Route path='*' element={<RootLayout />}>
          <Route path='login' element={<Login />} />
          <Route path='sign-up' element={<Login view={LoginView.SIGNUP} />} />
          <Route
            path='get-appointed'
            element={<Navigate to={`/get-appointed/${GLOBAL_GET_APPOINTED_SLUG}`} replace />}
          />
          <Route path='get-appointed/:slug' element={<GetAppointed />} />
          <Route path='logout' element={<LogoutPage />} />
          <Route path='login/callback' element={<LoginCallbackLazy />} />
          <Route element={<RequiredAuth />}>
            {/* Admin-only preview (opened in a new tab). Sits at a top-level URL rather than under
                portal-admin, but hits the authenticated preview endpoint, so it needs the same auth
                gate and role guard as the Get Appointed admin pages. */}
            <Route
              path='get-appointed-preview/:pageId'
              element={
                <RoleGuard routeSpecificRoles={PortalAdminPages[PortalAdminPageName.GetAppointedPages].roles}>
                  <GetAppointedPreview />
                </RoleGuard>
              }
            />
            {secureRoutes.map((route: IRoute) => {
              const RouteComponent =
                typeof route.component === 'function'
                  ? route.component({ isUnderwriter, isInternalUser })
                  : route.component;
              return route.outlets ? (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<RoleGuard routeSpecificRoles={route.roles}>{RouteComponent}</RoleGuard>}
                >
                  {/* Redirect when path matches exactly to the parent route */}
                  {route.indexPath && (
                    <Route index element={<Navigate to={`/${route.path}/${route.indexPath}`} replace />} />
                  )}
                  {/* Render other child routes */}
                  {route.outlets.map((outlet) => renderRoutes(route, outlet, route.roles))}
                  {/* Handle wildcard path */}
                  <Route
                    path='*'
                    element={<Navigate to={route.indexPath ? `/${route.path}/${route.indexPath}` : './'} replace />}
                  />
                </Route>
              ) : (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<RoleGuard routeSpecificRoles={route.roles}>{RouteComponent}</RoleGuard>}
                />
              );
            })}

            <Route
              path='*'
              element={
                <Forbidden
                  title={defaultTitle}
                  subtitle={defaultSubtitle}
                  items={defaultItems}
                  primaryBtnText='Continue to your Homepage'
                  primaryBtnRedirect='/'
                  secondaryBtnText='Log out'
                  secondaryBtnAction={async () => {
                    await oktaAuth.signOut();
                  }}
                />
              }
            />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export default AllRoutes;
